import type { EventMapFromUnion } from '@pkg/server/events'
import { createTypedNanoEvents } from '@pkg/server/events/adapters/createTypedNanoEvents'
import { logger } from '@pkg/shared/logger'
import type { SyncClientDatabase, SyncMessage } from '@pkg/sync/common'
import { walkEntityIds } from '@pkg/sync/common/walkEntityIds'
import { environmentManager } from '@tanstack/react-query'
import type { Emitter } from 'nanoevents'
import { Tab } from 'tab-election'
import { getTabId } from '../common/tabId'
import type { SyncRequest, SyncResponse } from '../common/types'
import { type CreatePeriodicIntervalParams, createPeriodicInterval } from './createPeriodicInterval'
import type { DatabaseInitializer } from './db-initializer'
import { createSyncClient, type SyncClient, type SyncClientConfig } from './syncClient'

export type TabStateBase = {
  tabId: string
  isLeader: boolean
}

export type SyncStartedEvent = { type: 'sync.started' }
export type SyncStoppedEvent = { type: 'sync.stopped' }

export type SyncInitProgressEvent = { type: 'sync.init.progress'; progress: number | null }
export type SyncInitStartedEvent = { type: 'sync.init.started' }
export type SyncInitFinishedEvent = { type: 'sync.init.finished'; isNew: boolean }
export type SyncInitErrorEvent = { type: 'sync.init.error'; error: Error }

export type SyncLeaderChangedEvent = { type: 'sync.leader.changed'; isLeader: boolean }
export type SyncTabsUpdatedEvent<TabState extends TabStateBase> = {
  type: 'sync.tabs.updated'
  tabs: TabState[]
}

/**
 * Entity ids that were changed locally and/or applied from the server.
 *
 * - `rows`: own row ids (`m.row`) of every message in the batch.
 * - `referencedIds`: ids extracted recursively from `m.value` whose prefix is
 *   in the runtime's `knownTables`. These surface FK relationships and ids
 *   nested inside JSON columns. Own row ids are excluded from this set.
 *
 * Emitted in every tab (leader broadcasts to followers).
 */
export type SyncRowsChangedEvent = { type: 'sync.rows.changed'; rows: string[]; referencedIds: string[] }

export type SyncSyncRequestedEvent = { type: 'sync.sync.requested'; reason: SyncReason }
export type SyncSyncStartedEvent = { type: 'sync.sync.started'; reason: SyncReason }
export type SyncSyncCompletedEvent = {
  type: 'sync.sync.completed'
  at: string
  reason: SyncReason
  newSyncTs?: string | null
}
export type SyncSyncErrorEvent = { type: 'sync.sync.error'; error: unknown }

export type SyncRuntimeEvent<TabState extends TabStateBase = TabStateBase> =
  // lifecycle
  | SyncStartedEvent
  | SyncStoppedEvent

  // db init (forwarded from initializer)
  | SyncInitProgressEvent
  | SyncInitStartedEvent
  | SyncInitFinishedEvent
  | SyncInitErrorEvent

  // leadership / tab state
  | SyncLeaderChangedEvent
  | SyncTabsUpdatedEvent<TabState>
  | SyncRowsChangedEvent

  // sync
  | SyncSyncRequestedEvent
  | SyncSyncStartedEvent
  | SyncSyncCompletedEvent
  | SyncSyncErrorEvent

export type SyncRuntimeEvents<TabState extends TabStateBase> = EventMapFromUnion<SyncRuntimeEvent<TabState>>

export type SyncReason = 'manual' | 'mutated' | 'remote' | 'periodic'

export type SyncRuntimeMessage =
  | {
      type: 'sync.sync.completed'
      at: string
      newSyncTs: string | null | undefined
    }
  | {
      type: 'sync.rows.changed'
      rows: string[]
      referencedIds: string[]
    }

type LeaderApi<TabState extends TabStateBase> = {
  syncNow: () => Promise<void>
  setTabState: (update: TabState) => Promise<void>
}

export type CreateSyncRuntimeParams<DB extends SyncClientDatabase, TabState extends TabStateBase> = Omit<
  SyncClientConfig<DB>,
  'sync' | 'onMessages'
> & {
  namespace: string

  /** Optional tab id; defaults to `getTabId()` (sessionStorage-based). */
  tabId?: string

  /** Database initializer to be executed on `start()`. */
  initializer: DatabaseInitializer

  /**
   * App-provided state to publish across tabs.
   * The runtime will inject `tabId` and the authoritative `isLeader`.
   */
  getTabState: () => Omit<TabState, 'tabId' | 'isLeader'>

  /**
   * Final network call. Only executed by the leader.
   *
   * Note: `SyncRequest` (from `syncClient`) does NOT include `clientStatus`.
   * If your API expects `clientStatus`, add it here.
   */
  sync: (req: Omit<SyncRequest, 'userId'>, tabs: TabState[]) => Promise<SyncResponse>

  /** Periodic syncing (idle by default; started on `start()` and only active in leader). */
  periodic?: Omit<CreatePeriodicIntervalParams, 'run'> | false

  /**
   * Optional external events system (e.g., app-wide event bus).
   * If provided, runtime events will be emitted here instead of creating a new emitter.
   * Leadership state will also be managed externally.
   */
  events?: {
    events: Emitter<SyncRuntimeEvents<TabState>>
    emit: <TType extends SyncRuntimeEvent<TabState>['type']>(
      type: TType,
      payload: Omit<Extract<SyncRuntimeEvent<TabState>, { type: TType }>, 'type'>,
    ) => void
    isLeader: () => boolean
  }
}

export type SyncRuntime<DB extends SyncClientDatabase, TabState extends TabStateBase> = Omit<SyncClient<DB>, 'sync'> & {
  tabId: string
  events: Emitter<SyncRuntimeEvents<TabState>>

  /**
   * Starts database initialization, leadership election wiring and (optionally) periodic sync.
   * Idempotent.
   */
  start: () => Promise<void>

  /** Stops periodic syncing and tears down leader election + subscriptions. Idempotent. */
  stop: () => void

  isLeader: () => boolean
  getTabsSnapshot: () => TabState[]
  publishTabState: () => Promise<void>
  notifyTabStateChanged: () => Promise<void>

  /** Unified sync entrypoint (always routes to leader when started). */
  sync: (reason?: SyncReason) => Promise<void>
}

export function createSyncRuntime<DB extends SyncClientDatabase, TabState extends TabStateBase>(
  params: CreateSyncRuntimeParams<DB, TabState>,
): SyncRuntime<DB, TabState> {
  logger.info(
    {
      namespace: params.namespace,
      tabId: params.tabId,
    },
    'Initializing sync runtime',
  )

  // Use provided events or create new ones
  const { events, emit } = params.events ?? createTypedNanoEvents<SyncRuntimeEvent<TabState>>()

  if (environmentManager.isServer()) {
    return {
      events,
    } as never // Running mutate or sync would not make sense on server
  }

  const tabId = params.tabId ?? getTabId()

  logger.info(
    {
      namespace: params.namespace,
      tabId,
    },
    'Sync runtime created',
  )

  const tab = new Tab<LeaderApi<TabState>>(params.namespace)

  let started = false
  // Use external isLeader if provided, otherwise manage internally
  let leader = false
  const getLeader = () => params.events?.isLeader() ?? leader
  let stopPeriodic: (() => void) | undefined

  const tabStateByTabId = new Map<string, Omit<TabState, 'tabId'>>()

  const getTabsSnapshot = (): TabState[] => {
    return [...tabStateByTabId.entries()].map(([tabId, v]) => ({
      tabId,
      ...v,
    })) as TabState[]
  }

  const setLeader = (value: boolean) => {
    const currentLeader = getLeader()
    if (currentLeader === value) return

    // Only update internal state if not using external events
    if (!params.events) {
      leader = value
    }

    logger.info(
      {
        namespace: params.namespace,
        tabId,
        isLeader: value,
      },
      'Leader state changed',
    )

    emit('sync.leader.changed', { isLeader: value })
  }

  const storeTabState = (state: TabState) => {
    const { tabId, ...rest } = state
    tabStateByTabId.set(tabId, rest)

    logger.debug(
      {
        namespace: params.namespace,
        tabId,
        updatedTabId: tabId,
        tabsCount: tabStateByTabId.size,
      },
      'Tab state updated',
    )

    emit('sync.tabs.updated', { tabs: getTabsSnapshot() })
  }

  const buildMyTabState = (): TabState => {
    const extra = params.getTabState()
    return {
      tabId,
      ...extra,
      // runtime is the source of truth for leadership
      isLeader: getLeader(),
    } as TabState
  }

  const publishTabState = async () => {
    if (!started) return

    logger.debug(
      {
        namespace: params.namespace,
        tabId,
        isLeader: getLeader(),
      },
      'Publishing tab state to leader',
    )

    await tab.call('setTabState', buildMyTabState())
  }

  const knownTables = new Set(Object.keys(params.schema ?? {}))

  const collectChangedIds = (messages: readonly SyncMessage[]): { rows: string[]; referencedIds: string[] } => {
    const rowsSet = new Set<string>()
    for (const m of messages) rowsSet.add(m.row)

    const refs = new Set<string>()
    if (knownTables && knownTables.size > 0) {
      for (const m of messages) walkEntityIds(m.value, knownTables, refs)
      // Own row ids are already in `rows` — drop from referenced to keep the
      // two sets disjoint.
      for (const r of rowsSet) refs.delete(r)
    }

    return { rows: [...rowsSet], referencedIds: [...refs] }
  }

  const client = createSyncClient({
    sync: req => params.sync(req, getTabsSnapshot()),
    db: params.db,
    userId: params.userId,
    nodeId: params.nodeId,
    hlc: params.hlc,
    schema: params.schema,
    onMessages: (_trx, messages) => {
      const { rows, referencedIds } = collectChangedIds(messages)
      if (rows.length > 0) {
        emit('sync.rows.changed', { rows, referencedIds })
      }

      // Mutations should request a sync via the leader.
      // Note: if runtime is not started yet, there is no leader wiring, so we only log.
      if (!started) {
        logger.debug(
          {
            namespace: params.namespace,
            tabId,
          },
          'Local messages produced before runtime start; sync will not be requested yet',
        )
        return
      }

      logger.debug(
        {
          namespace: params.namespace,
          tabId,
        },
        'Local messages produced; requesting leader sync',
      )

      void tab.call('syncNow')
    },

    onSynced: ({ response }) => {
      // Note: local mutations are already emitted via `onMessages` above. We keep this
      // focused on server-applied changes.

      // The leader is the only one executing network sync in normal operation.
      // Still, we emit in the leader and broadcast to all followers.
      const { rows, referencedIds } = collectChangedIds(response.messages ?? [])
      if (rows.length === 0) return

      emit('sync.rows.changed', { rows, referencedIds })

      if (getLeader()) {
        tab.send({
          type: 'sync.rows.changed',
          rows,
          referencedIds,
        } satisfies SyncRuntimeMessage)
      }
    },
  })

  const sync = async (reason: SyncReason = 'manual') => {
    const syncStartedAt = Date.now()

    emit('sync.sync.requested', { reason })
    emit('sync.sync.started', { reason })

    logger.debug(
      {
        namespace: params.namespace,
        tabId,
        reason,
        started,
        isLeader: getLeader(),
      },
      'Sync requested',
    )

    try {
      if (!started) {
        // Not started means: no leader election wiring yet.
        // We still allow explicit manual sync to work (useful in tests).
        await client.sync()

        logger.info(
          {
            namespace: params.namespace,
            tabId,
            reason,
            durationMs: Date.now() - syncStartedAt,
            routedToLeader: false,
          },
          'Sync finished',
        )

        emit('sync.sync.completed', { at: new Date().toISOString(), reason })
        return
      }

      await tab.call('syncNow')

      logger.debug(
        {
          namespace: params.namespace,
          tabId,
          reason,
          durationMs: Date.now() - syncStartedAt,
          routedToLeader: true,
        },
        'Sync finished',
      )

      // Followers will also receive a broadcast message from the leader.
      emit('sync.sync.completed', { at: new Date().toISOString(), reason })
    } catch (err) {
      logger.warn(
        {
          namespace: params.namespace,
          tabId,
          reason,
          durationMs: Date.now() - syncStartedAt,
          err,
        },
        'Sync failed',
      )

      emit('sync.sync.error', { error: err })
      throw err
    }
  }

  const notifyTabStateChanged = async () => {
    await publishTabState()
    await sync('manual')
  }

  const onMessage = (event: MessageEvent) => {
    const msg = event.data as SyncRuntimeMessage
    if (msg?.type === 'sync.sync.completed') {
      logger.debug(
        {
          namespace: params.namespace,
          tabId,
          at: msg.at,
          newSyncTs: msg.newSyncTs,
        },
        'Received synced broadcast from leader',
      )

      // Note: the broadcast doesn't carry a reason; keep the local reason.
      emit('sync.sync.completed', { at: msg.at, reason: 'manual', newSyncTs: msg.newSyncTs })
      return
    }

    if (msg?.type === 'sync.rows.changed') {
      if (msg.rows.length > 0) {
        emit('sync.rows.changed', { rows: msg.rows, referencedIds: msg.referencedIds ?? [] })
      }
    }
    return
  }

  const startPeriodicIfLeader = () => {
    stopPeriodic?.()
    stopPeriodic = undefined

    if (params.periodic === false) {
      return
    }
    if (!getLeader()) return

    logger.info(
      {
        namespace: params.namespace,
        tabId,
      },
      'Starting periodic sync interval (leader only)',
    )
    tab.send('syncNow')

    stopPeriodic = createPeriodicInterval({
      ...params.periodic,
      run: () => sync('periodic'),
    })
  }

  const start = async () => {
    if (started) return
    started = true
    emit('sync.started', {})

    logger.info(
      {
        namespace: params.namespace,
        tabId,
      },
      'Sync runtime started',
    )

    await params.initializer.initialize({
      onProgress: progress => emit('sync.init.progress', { progress }),
      onInitStarted: () => emit('sync.init.started', {}),
      onInitFinished: isNew => emit('sync.init.finished', { isNew }),
      onError: error => emit('sync.init.error', { error }),
    })

    logger.info(
      {
        namespace: params.namespace,
        tabId,
      },
      'Database initialization finished',
    )

    tab.addEventListener('message', onMessage)

    void tab.waitForLeadership(() => {
      logger.info(
        {
          namespace: params.namespace,
          tabId,
        },
        'Leadership acquired',
      )

      setLeader(true)

      // Ensure leader has itself in the snapshot immediately.
      storeTabState(buildMyTabState())
      startPeriodicIfLeader()

      return {
        syncNow: async () => {
          logger.debug(
            {
              namespace: params.namespace,
              tabId,
            },
            'Leader executing syncNow',
          )

          await client.sync()

          // Broadcast sync completion for follower invalidation.
          // Note: `newSyncTs` currently not available without additional plumbing.
          tab.send({
            type: 'sync.sync.completed',
            at: new Date().toISOString(),
            newSyncTs: undefined,
          } satisfies SyncRuntimeMessage)
        },
        setTabState: (update: TabState) => {
          storeTabState(update)
        },
      }
    })

    const onFocus = () => publishTabState()
    window.addEventListener('focus', onFocus)

    // Best-effort initial publish.
    void publishTabState()

    // Ensure stop() removes focus listener.
    const originalStop = stop
    stop = () => {
      window.removeEventListener('focus', onFocus)
      originalStop()
    }
  }

  // `stop` is reassigned in `start` to include focus cleanup.
  let stop = () => {
    if (!started) return
    started = false

    logger.info(
      {
        namespace: params.namespace,
        tabId,
      },
      'Sync runtime stopped',
    )

    if (stopPeriodic) {
      logger.info(
        {
          namespace: params.namespace,
          tabId,
        },
        'Stopped periodic sync interval',
      )
    }

    stopPeriodic?.()
    stopPeriodic = undefined

    tab.removeEventListener('message', onMessage)
    tab.close()

    if (getLeader()) setLeader(false)

    emit('sync.stopped', {})
  }

  return {
    ...client,
    tabId,
    events,
    start,
    stop,
    isLeader: getLeader,
    getTabsSnapshot,
    publishTabState,
    notifyTabStateChanged,
    sync,
  }
}
